// „Прехвърляне" — ОПЕРАЦИЯТА: начален екран вместо таблица (таблицата е в
// справката „Прехвърляния"). Показва незавършеното ми прехвърляне и тези на
// колегите (черновите — секцията я има само когато има какво да се
// продължи), прехвърлените днес от ВСИЧКИ складове и води към новото
// прехвърляне („+ Ново" при незавършено пита: продължи го или го изтрий).
// След запис листът се връща тук със `state.openDoc` → toast „Виж документа".
import { useEffect, type FC } from "react";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../hooks/useViewport";
import { useTransfersReport } from "../../../hooks/useInventoryTransfers";
import type { TransferDocument } from "../../../api/inventoryTransfers";
import { PageHeader } from "../components/list/PageHeader";
import { PRIMARY_BUTTON_CLASS, SECONDARY_BUTTON_CLASS } from "../components/list/buttonStyles";
import { InventoryTheme } from "../components/InventoryTheme";
import { useActionToast } from "../components/useActionToast";
import { periodOfPreset, periodToParams } from "../components/period/periodPresets";
import { DraftHomeSection } from "../components/drafts/DraftHomeSection";
import { DraftHomeModals } from "../components/drafts/DraftHomeModals";
import { DRAFT_TEXTS } from "../components/drafts/draftTexts";
import { useTransferDocument } from "./components/useTransferDocument";
import { TodayTransfersSection } from "./components/home/TransferHomeSections";
import { useTransferHomeDrafts } from "./components/home/useTransferHomeDrafts";
import { enteredAtOf, isTransferAnnulled } from "./components/transferMeta";

// Проповете от layout-а (Inventory.tsx) не ѝ трябват — всичко идва от
// useInventoryShell; типът само ги приема.
type LayoutProps = { warehouseId?: number | null };

export const TransferHomePage: FC<LayoutProps> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isNarrow } = useViewport();
  const showToast = useActionToast();
  const transferDocument = useTransferDocument();
  const drafts = useTransferHomeDrafts();

  // „Прехвърлени днес" — всички складове (без warehouseId), по кога е въведено
  // (BE с редизайна), иначе по датата на документа
  const today = dayjs();
  const { data: todayRaw = [] } = useTransfersReport(periodToParams(periodOfPreset("today", today)));
  const todayTransfers = todayRaw
    .filter(
      (transfer) =>
        !isTransferAnnulled(transfer) && dayjs(enteredAtOf(transfer)).isSame(today, "day"),
    )
    .sort((a, b) => enteredAtOf(b).localeCompare(enteredAtOf(a)));

  // След запис: state.openDoc → toast с „Виж документа"
  useEffect(() => {
    const state = location.state as { openDoc?: TransferDocument } | null;
    if (!state?.openDoc) return;
    const saved = state.openDoc;
    navigate(location.pathname, { replace: true, state: null });
    drafts.refreshLocal();
    showToast({
      text: t("inventoryPage.trxSavedToast", { number: saved.id }),
      actionLabel: t("inventoryPage.postingViewDocument"),
      onAction: () => transferDocument.open(saved, { editable: false }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // При незавършено — първо въпросът (`drafts.ask`)
  const startNew = () => drafts.startNew();

  return (
    <InventoryTheme>
      <div className="flex h-full flex-col bg-canvas text-ink">
        <PageHeader
          title={t("inventoryPage.transfer")}
          subtitle={
            drafts.mine
              ? `${t(DRAFT_TEXTS.transfer.hasDraft)} · ${t("inventoryPage.trxHomeSubtitle")}`
              : t("inventoryPage.trxHomeSubtitle")
          }
          right={
            <>
              <button
                type="button"
                onClick={() => navigate("/inventory/transfers")}
                className={SECONDARY_BUTTON_CLASS}
              >
                {t("inventoryPage.homeAllTransfers")}
              </button>
              <button type="button" onClick={startNew} className={PRIMARY_BUTTON_CLASS}>
                {t("inventoryPage.newTransferPlus")}
              </button>
            </>
          }
        />
        <div
          className={`flex min-h-0 flex-1 flex-col gap-[26px] overflow-y-auto ${
            isNarrow ? "px-3 pb-10 pt-3.5" : "px-7 pb-12 pt-5"
          }`}
        >
          <DraftHomeSection
            mine={drafts.mine}
            others={drafts.others}
            texts={DRAFT_TEXTS.transfer}
            onNew={startNew}
          />
          <TodayTransfersSection
            transfers={todayTransfers}
            onView={(transfer) => transferDocument.open(transfer)}
            onNew={startNew}
          />
        </div>
        <DraftHomeModals drafts={drafts} texts={DRAFT_TEXTS.transfer} />
        {transferDocument.modals}
      </div>
    </InventoryTheme>
  );
};

export default TransferHomePage;
